import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from '../api/axiosInstance';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";

function formatLocalDateTime(input) {
  const dt = new Date(input);
  const offset = dt.getTimezoneOffset();
  const localDate = new Date(dt.getTime() - offset * 60000);
  return localDate.toISOString().slice(0, 16);
}

function WeightHistory() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [error, setError] = useState("");
  const [userName, setUserName] = useState("訪客");
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return d.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(() =>
    new Date().toISOString().split("T")[0]
  );
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [displayUnit, setDisplayUnit] = useState("kg"); // 'kg' | 'lb' | 'jin'

  const fetchRecords = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `/api/Weight?startDate=${startDate}&endDate=${endDate}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setRecords(res.data);
      setError("");
    } catch {
      setError("查詢失敗，請重新登入或稍後再試。");
    }
  };

  const handleEdit = (record) => {
    const kg = parseFloat(record.weightInKg);
    setEditingId(record.id);
    setEditData({
      recordedAt: formatLocalDateTime(record.recordedAt),
      weightInKg: kg.toString(),
      pound: (kg * 2.20462).toFixed(1),
      jin: (kg * 2).toFixed(1),
      notes: record.notes || "",
    });
  };

  const handleUnitChange = (e) => {
    setDisplayUnit(e.target.value);
  };

  const convert = (kg, unit) => {
    if (unit === "kg") return kg;
    if (unit === "lb") return (kg * 2.20462).toFixed(1);
    if (unit === "jin") return (kg * 2).toFixed(1);
    return kg;
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    if (name === "weightInKg") {
      setEditData({
        ...editData,
        weightInKg: value,
        pound: value ? (parseFloat(value) * 2.20462).toFixed(1) : '',
        jin: value ? (parseFloat(value) * 2).toFixed(1) : '',
      });
    } else if (name === "pound") {
      const kg = value ? (parseFloat(value) / 2.20462).toFixed(2) : '';
      setEditData({
        ...editData,
        pound: value,
        weightInKg: kg,
        jin: kg ? (parseFloat(kg) * 2).toFixed(1) : '',
      });
    } else if (name === "jin") {
      const kg = value ? (parseFloat(value) / 2).toFixed(2) : '';
      setEditData({
        ...editData,
        jin: value,
        weightInKg: kg,
        pound: kg ? (parseFloat(kg) * 2.20462).toFixed(1) : '',
      });
    } else {
      setEditData({ ...editData, [name]: value });
    }
  };

  const handleSave = async (id) => {
    if (!window.confirm("確定要儲存修改？")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.put(`/api/Weight/${id}`, {
        ...editData,
        weightInKg: parseFloat(editData.weightInKg),
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEditingId(null);
      fetchRecords();
    } catch {
      alert("更新失敗！");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("確定要刪除這筆紀錄？")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/Weight/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchRecords();
    } catch {
      alert("刪除失敗！");
    }
  };

  useEffect(() => {
    document.title = "歷史體重紀錄";
    fetchRecords();

    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (user?.name) {
        setUserName(user.name);
      }
    } catch (err) {
      console.error("讀取 user 發生錯誤", err);
    }
  }, []);

  return (
    <div className="container mt-4">
      <h2 className="mb-4">體重紀錄 - {userName}</h2>

      <div className="mb-3">
        <button className="btn btn-success me-2" onClick={() => navigate("/weight/new")}>
          新增體重記錄
        </button>
        <button className="btn btn-outline-secondary" onClick={() => navigate("/dashboard")}>
          回到目錄
        </button>
      </div>

      <div className="row g-2 align-items-end mb-3">
        <div className="col-auto">
          <label className="form-label">起始日期</label>
          <input
            type="date"
            className="form-control"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="col-auto">
          <label className="form-label">結束日期</label>
          <input
            type="date"
            className="form-control"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="col-auto">
          <button className="btn btn-primary" onClick={fetchRecords}>
            查詢
          </button>
        </div>
        <div className="col-auto">
          <label className="form-label">顯示單位</label>
          <select className="form-select" value={displayUnit} onChange={handleUnitChange}>
            <option value="kg">公斤 (kg)</option>
            <option value="lb">磅 (lb)</option>
            <option value="jin">斤</option>
          </select>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {records.length > 0 && (
        <div style={{ width: "100%", height: 300 }}>
          <ResponsiveContainer>
            {/* <LineChart data={records}> */}
            <LineChart data={[...records].reverse()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="recordedAt"
                tickFormatter={(value) =>
                  new Date(value).toLocaleString("zh-TW", {
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                }
              />
              <YAxis />
              <Tooltip
                labelFormatter={(value) =>
                  new Date(value).toLocaleString("zh-TW", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                }
              />
              <Legend />
              <Line
                type="monotone"
                dataKey={(r) => parseFloat(convert(r.weightInKg, displayUnit))}
                stroke="#3498db"
                name={`體重 (${displayUnit})`}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-bordered table-striped table-hover">
          <thead className="table-light">
            <tr>
              <th>日期時間</th>
              <th>公斤 (kg)</th>
              <th>磅 (lb)</th>
              <th>斤</th>
              <th>備註</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {records.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center">尚無資料</td>
              </tr>
            ) : (
              records.map((r) =>
                editingId === r.id ? (
                  <tr key={r.id}>
                    <td>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={editData.recordedAt}
                        onChange={(e) =>
                          setEditData({ ...editData, recordedAt: e.target.value })
                        }
                      />
                    </td>
                    <td>
                      <input
                        className="form-control"
                        name="weightInKg"
                        value={editData.weightInKg}
                        onChange={handleFieldChange}
                      />
                    </td>
                    <td>
                      <input
                        className="form-control"
                        name="pound"
                        value={editData.pound}
                        onChange={handleFieldChange}
                      />
                    </td>
                    <td>
                      <input
                        className="form-control"
                        name="jin"
                        value={editData.jin}
                        onChange={handleFieldChange}
                      />
                    </td>
                    <td>
                      <input
                        className="form-control"
                        name="notes"
                        value={editData.notes}
                        onChange={handleFieldChange}
                      />
                    </td>
                    <td>
                      <button
                        className="btn btn-success btn-sm me-1"
                        onClick={() => handleSave(r.id)}
                      >
                        儲存
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setEditingId(null)}
                      >
                        取消
                      </button>
                    </td>
                  </tr>
                ) : (
                  <tr key={r.id}>
                    <td>{new Date(r.recordedAt).toLocaleString("zh-TW", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit"
                    })}</td>
                    <td>{parseFloat(r.weightInKg).toFixed(1)}</td>
                    <td>{(r.weightInKg * 2.20462).toFixed(1)}</td>
                    <td>{(r.weightInKg * 2).toFixed(1)}</td>
                    <td>{r.notes}</td>
                    <td>
                      {r.source === r.userId && (
                        <>
                          <button className="btn btn-outline-primary btn-sm me-1" onClick={() => handleEdit(r)}>編輯</button>
                          <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(r.id)}>刪除</button>
                        </>
                      )}
                    </td>
                  </tr>
                )
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default WeightHistory;
